import Decimal from "decimal.js";

type NullableNum = string | number | null | undefined;

const DEFAULT_SCALE = 8;

// "asym" を追加
// - 正: 0方向 (ROUND_DOWN)
// - 負: -∞方向 (ROUND_FLOOR)
export type RoundMode = "trunc" | "ceil" | "asym";

// 丸め方法を決める（asym は x の符号で分岐するので別関数へ）
function toRounding(mode: Exclude<RoundMode, "asym">): Decimal.Rounding {
  switch (mode) {
    case "trunc":
      return Decimal.ROUND_DOWN; // 0方向
    case "ceil":
      return Decimal.ROUND_CEIL; // +∞ 方向
  }
}

// 符号依存の非対称丸めを含めて scale 桁へ丸める
export function roundToScale(x: Decimal, scale: number, mode: RoundMode): Decimal {
  if (mode === "asym") {
    const rounding = x.isNegative() ? Decimal.ROUND_FLOOR : Decimal.ROUND_DOWN;
    return x.toDecimalPlaces(scale, rounding);
  }
  return x.toDecimalPlaces(scale, toRounding(mode));
}

export function toDec(v: NullableNum): Decimal {
  if (v === null || v === undefined) return new Decimal(0);
  if (typeof v === "string") {
    const s = v.trim();
    if (s === "") return new Decimal(0);
    return new Decimal(s);
  }
  return new Decimal(v);
}

export function pad(v: NullableNum, scale: number): string {
  if (v === null || v === undefined) {
    return new Decimal(0).toFixed(scale);
  }

  const s = typeof v === "string" ? v.trim() : v;
  if (s === "") {
    return new Decimal(0).toFixed(scale);
  }

  return new Decimal(s).toFixed(scale);
}

export function bcadd(
  a: NullableNum,
  b: NullableNum,
  scale = DEFAULT_SCALE,
  mode: RoundMode = "asym"
): string {
  const r = toDec(a).plus(toDec(b));
  return roundToScale(r, scale, mode).toFixed(scale);
}

export function bcsub(
  a: NullableNum,
  b: NullableNum,
  scale = DEFAULT_SCALE,
  mode: RoundMode = "asym"
): string {
  const r = toDec(a).minus(toDec(b));
  return roundToScale(r, scale, mode).toFixed(scale);
}

export function bcmul(
  a: NullableNum,
  b: NullableNum,
  scale = DEFAULT_SCALE,
  mode: RoundMode = "asym"
): string {
  const r = toDec(a).times(toDec(b));
  return roundToScale(r, scale, mode).toFixed(scale);
}

export function bcdiv(
  a: NullableNum,
  b: NullableNum,
  scale = DEFAULT_SCALE,
  mode: RoundMode = "asym"
): string {
  const divisor = toDec(b);
  if (divisor.isZero()) throw new Error("Division by zero");

  const extra = 6;
  const r = toDec(a).div(divisor);

  // 先に多めの桁 → 最終桁に丸め（モードに従う）
  // asym は符号依存なので、ここでも roundToScale を使って統一する
  const pre = roundToScale(r, scale + extra, mode);
  return roundToScale(pre, scale, mode).toFixed(scale);
}

export function bccomp(
  a: NullableNum,
  b: NullableNum,
  scale = DEFAULT_SCALE,
  mode: RoundMode = "asym"
): number {
  const aa = roundToScale(toDec(a), scale, mode);
  const bb = roundToScale(toDec(b), scale, mode);
  return aa.comparedTo(bb);
}
